import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@bufferapp/components';
import MessageIcon from '@bufferapp/ui/Icon/Icons/Message';
import PersonIcon from '@bufferapp/ui/Icon/Icons/Person';
import { Tooltip } from '@bufferapp/ui';
import { grayDarker } from '@bufferapp/ui/style/colors';
import ConfirmDeleteButton from '../ConfirmDeleteButton';
import {
  ALLOWED_COLORS,
  ButtonStyled,
  ButtonWrapper,
  CardFooterContent,
  CardFooterWrapper,
  CardFooterWrapperWithBorder,
  CommentIconWrapper,
  DraftActionButton,
  EditButton,
  IconWrapper,
  Message,
  PreMessage,
  RequeueButton,
  UserTagsIconWrapper,
} from './styles';

const CardFooter = ({
  hideButtons,
  icon,
  preMessage,
  message,
  messageColor,
  messageLink,
  onDeleteClick,
  onEditClick,
  onSubmitClick,
  submitLabel,
  onRequeueClick,
  requeueLabel,
  onRevertApprovalClick,
  onDraftActionButtonClick,
  submitDraftsLabel,
  hasFirstComment,
  isPerformingAction,
  actionMessage,
  isDragging,
  disableBorder,
  hasUserTags,
  shouldShowEditButton,
  draftActionButtonTooltipMessage,
  draftActionButtonDisabled,
  children,
}) => {
  const hideAllButtons = hideButtons || isPerformingAction || messageLink;
  const textColor = ALLOWED_COLORS.includes(messageColor)
    ? messageColor
    : grayDarker;
  const WrapperComponent = disableBorder
    ? CardFooterWrapper
    : CardFooterWrapperWithBorder;

  if (children) {
    return (
      <WrapperComponent isDragging={isDragging} disableBorder={disableBorder}>
        {children}
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent isDragging={isDragging} disableBorder={disableBorder}>
      {/* FOOTER MESSAGE */}
      <CardFooterContent>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        {messageLink ? (
          <Link href={messageLink} unstyled newTab>
            <Message type="p" textColor={textColor}>
              {message}
            </Message>
          </Link>
        ) : (
          <>
            {preMessage && (
              <PreMessage type="p">
                {preMessage}
                &nbsp;
              </PreMessage>
            )}
            <Message type="p" textColor={textColor}>
              {message}
            </Message>
          </>
        )}
        {hasFirstComment && (
          <CommentIconWrapper title="Post includes a comment">
            <MessageIcon />
          </CommentIconWrapper>
        )}
        {hasUserTags && (
          <UserTagsIconWrapper
            title="Post includes tagged users"
            hasFirstComment={hasFirstComment}
          >
            <PersonIcon />
          </UserTagsIconWrapper>
        )}
      </CardFooterContent>

      {/* FOOTER BUTTONS */}
      {!hideAllButtons && (
        <ButtonWrapper>
          {onDeleteClick && (
            <>
              <ConfirmDeleteButton onDeleteClick={onDeleteClick} />
            </>
          )}
          {shouldShowEditButton && onEditClick && (
            <>
              <EditButton
                type="text"
                label="Edit"
                size="small"
                onClick={onEditClick}
                data-testid="shared-components-edit-btn"
              />
            </>
          )}

          {/* POSTS AND STORIES ACTIONS ONLY */}
          {onSubmitClick && (
            <ButtonStyled
              type="secondary"
              label={submitLabel}
              size="small"
              onClick={onSubmitClick}
            />
          )}
          {onRequeueClick && (
            <RequeueButton
              type="primary"
              label={requeueLabel}
              size="small"
              onClick={onRequeueClick}
            />
          )}

          {/* DRAFTS ACTIONS ONLY */}
          {onRevertApprovalClick && (
            <ButtonStyled
              type="secondary"
              label="Move to Drafts"
              size="small"
              onClick={onRevertApprovalClick}
            />
          )}
          {onDraftActionButtonClick && (
            <Tooltip label={draftActionButtonTooltipMessage} position="bottom">
              <DraftActionButton
                type="secondary"
                label={submitDraftsLabel}
                size="small"
                onClick={onDraftActionButtonClick}
                disabled={draftActionButtonDisabled}
              />
            </Tooltip>
          )}
        </ButtonWrapper>
      )}

      {/* FOOTER ACTONS */}
      {isPerformingAction && <Message type="p">{actionMessage}</Message>}
    </WrapperComponent>
  );
};

CardFooter.propTypes = {
  hideButtons: PropTypes.bool,
  icon: PropTypes.node,
  preMessage: PropTypes.string,
  message: PropTypes.string,
  messageColor: PropTypes.string,
  messageLink: PropTypes.string,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onSubmitClick: PropTypes.func,
  submitLabel: PropTypes.string,
  onRequeueClick: PropTypes.func,
  requeueLabel: PropTypes.string,
  onRevertApprovalClick: PropTypes.func,
  onDraftActionButtonClick: PropTypes.func,
  submitDraftsLabel: PropTypes.string,
  hasFirstComment: PropTypes.bool,
  isPerformingAction: PropTypes.bool,
  actionMessage: PropTypes.string,
  isDragging: PropTypes.bool,
  disableBorder: PropTypes.bool,
  hasUserTags: PropTypes.bool,
  shouldShowEditButton: PropTypes.bool,
  draftActionButtonTooltipMessage: PropTypes.string,
  draftActionButtonDisabled: PropTypes.bool,
  children: PropTypes.node,
};

CardFooter.defaultProps = {
  hideButtons: false,
  icon: null,
  preMessage: null,
  message: '',
  messageColor: grayDarker,
  messageLink: null,
  onDeleteClick: null,
  onEditClick: null,
  onSubmitClick: null,
  submitLabel: 'Share Now',
  onRequeueClick: null,
  requeueLabel: 'Reschedule',
  onRevertApprovalClick: null,
  onDraftActionButtonClick: null,
  submitDraftsLabel: 'Approve',
  hasFirstComment: false,
  isPerformingAction: false,
  actionMessage: 'Submitting...',
  isDragging: false,
  disableBorder: false,
  hasUserTags: false,
  shouldShowEditButton: true,
  draftActionButtonTooltipMessage: null,
  draftActionButtonDisabled: true,
  children: null,
};

export default CardFooter;
